import {
  SocialNetwork,
  Profile,
  PricePlanTier,
  MediaGroup,
  SendModeType,
  ReviewStatus,
  SocialNetworkMessage,
  Media,
} from "@/interfaces";
import { SCAccount } from "@/providers";
import { Plan } from "./Plan";

type PaymentStatus = "SUCCESSFUL" | "FAILED" | "AVAILABLE" | "TRANSFERRED";

export type ReadyToPostContentType = "PROMPT" | "VIDEO_PROJECT" | "POST";

export enum SubscriptionType {
  ENTERPRISE = "ENTERPRISE",
  SINGLE = "SINGLE",
}

export enum SubscriptionStatus {
  PENDING_PAYMENT = "PENDING_PAYMENT",
  FREE_TRIAL = "FREE_TRIAL",
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  CANCELED = "CANCELED",
}

export interface BasePlayer {
  id: number;
  email?: string;
  firstName: string;
  lastName: string;
  phone?: string;
  photoUrl?: string;
  creationDate?: string;
  leadsNeedToCloseTransaction?: number;
  averageIncomesPerTransaction?: number;
  transactionsClosedLastYear?: number;
  webAddress?: string | null;
  addHashTagAutomatically?: boolean;
}

export interface AccountResumen {
  user: SCAccount;
  profile: Profile;
  player?: BasePlayer;
}
export interface PlayerAccount {
  userId: number;
  playerId?: number;
  username?: string;
  email?: string;
  firstName: string;
  lastName: string;
  phone?: string;
  photoUrl?: string;
  status?: string;
  socialNetworks?: SocialNetwork[];
  latestLoginDate?: string;
  verificationDate?: string;
  createdDate?: string;
  addHashTagAutomatically?: boolean;
  complianceDisclaimer?: string;
  internalIdentifier?: string;
  title?: string;
  coachId?: string;
}

export interface PlayerSearchResponse {
  playerId: number;
  userId: number;
  phone?: string;
  email?: string;
  photoUrl?: string;
  firstName: string;
  lastName: string;
  socialNetworks?: SocialNetwork[];
  lastLoginDate?: string;
  verificationDate?: string;
  subscriptionSummary?: SubscriptionSummary;
  coachProfile?: SubscriptionCoachInfo;
}

interface SubscriptionCoachInfo {
  coachId: number;
  email?: string;
  firstName: string;
  lastName: string;
  companyName: string;
}

interface SubscriptionSummary {
  type: SubscriptionType;
  status: SubscriptionStatus;
  tier?: PricePlanTier;
}

interface PlayerPlanSubscription {
  id: number;
  playerId: number;
  pricePlan: Plan;
  status: SubscriptionStatus;
  endPeriodDate?: string;
}

interface PromptActivity {
  playerId: number;
  countPromptReceived: number;
  countPromptPosted: number;
  lastSuccessfulDeliveryDate: string;
  lastScheduledDeliveryDate: string;
}

export interface SubscribedPlayerSearchResponse {
  playerAccount: PlayerAccount;
  lastPaymentStatus?: PaymentStatus;
  canceledDate?: string;
  pricePlanSubscription?: PlayerPlanSubscription;
  promptActivity?: PromptActivity;
  branchNames?: string[];
  regionNames?: string[];
  stateCodes?: string[];
  costCenterNames?: string[];
  industryRoleNames?: string[];
}

interface TotalStats {
  countPromptPosted: number;
  likes: number;
  followers: number;
  comments: number;
  shares: number;
  views: number;
  click: number;
}

export interface PlayerResumeStats extends TotalStats {
  statsBySocialNetwork?: { [key: string]: TotalStats };
}

export interface PlayerWithStats {
  photoUrl?: string;
  info: PlayerAccount;
  email: string;
  countPromptReceived: number;
  countPromptPosted: number;
  likes: number;
  followers: number;
  comments: number;
  shares: number;
  views: number;
  click: number;
  statsBySocialNetwork: { [key: string]: TotalStats };
  countPromptPostedSplitted: { [key: string]: number };
  branchNames?: string[];
  regionNames?: string[];
  costCenterNames?: string[];
  playerDisclaimer?: string;
}

export interface PlayerWithCampaignStats {
  photoUrl?: string;
  info: PlayerAccount;
  email: string;
  countPromptReceived: number;
  countPromptPosted: number;
  countPromptRemaining?: number;
}

export interface PlayerWithBranch {
  photoUrl?: string;
  info: PlayerAccount;
  email: string;
  regionNames: string[];
  branchNames: string[];
  stateCodes: string[];
}

export interface Region {
  regionId: string;
  coachId: string;
  name: string;
}

export interface State {
  stateId: string;
  coachId: string;
  stateCode: string;
}

export interface Branch {
  branchId: string;
  coachId: string;
  name: string;
}

export interface CostCenter {
  costCenterId: string;
  coachId: string;
  name: string;
}

export interface IndustryRole {
  industryRoleId: string;
  coachId: string;
  name: string;
}

export interface PlayerIndustryRoles {
  industryRoles: IndustryRole[];
  availableIndustryRoles: IndustryRole[];
}

export interface PostReviewEvent {
  id: number;
  userId: number;
  username: string;
  userRole: "PLAYER" | "COACH";
  message?: string;
  status?: ReviewStatus;
  postId: number;
  createdDate: string;
}
export interface PlayerPost {
  createdDate: string;
  keyphrases: string[];
  mediaGroup: MediaGroup;
  scheduledDatetimeUtc: any;
  message: string;
  playerId: number;
  postId: number;
  promptId?: number;
  sendMode: SendModeType;
  campaignPlayerId?: number;
  hashtags?: string;
  coachPostReviewStatus?: ReviewStatus;
  reviewStatus?: ReviewStatus;
  socialNetworks: SocialNetworkMessage[];
  postReviewEvents?: PostReviewEvent[];
}

export interface PostReadyContent {
  title: string;
  subtitle: string;
  contentType: ReadyToPostContentType;
  contentId: number;
  media: Media;
}
